import type { WeldSample } from './weldTxtParser'

export interface SlopeTimeConfig {
  inSec: number
  outSec: number
}

export interface SlopeFilterResult {
  kept: WeldSample[]
  droppedHead: number
  droppedTail: number
  /**
   * `true` when the combined slope window (`inSec + outSec`) is wider than
   * the weld duration on the machine clock — in that case we fall back to
   * keeping all samples rather than silently dropping every row.
   */
  windowExceedsDuration: boolean
}

type EventWindow = {
  sample: WeldSample
  startSec: number
  endSec: number
}

/**
 * Drop samples in the first `inSec` and last `outSec` seconds of a weld
 * upload before the aggregator computes min/avg/max. Operates on the whole
 * unioned batch (not per pass) by design — slope-in/slope-out is a
 * per-upload setting on the FloorSheet.
 *
 * Uses the machine weld clock from TXT columns `Start Time`, `Time`, and
 * `Duration` — not wall-clock `recordedAt`. Each row is an event window
 * `[Start Time, Time]` (or `Start Time + Duration` when `Time` is missing).
 * Samples without a usable `Start Time` are always preserved.
 */
export function applySlopeFilter(
  samples: WeldSample[],
  cfg: SlopeTimeConfig
): SlopeFilterResult {
  const inSec = sanitizeSec(cfg?.inSec)
  const outSec = sanitizeSec(cfg?.outSec)

  if (samples.length === 0) {
    return { kept: [], droppedHead: 0, droppedTail: 0, windowExceedsDuration: false }
  }

  if (inSec === 0 && outSec === 0) {
    return {
      kept: samples.slice(),
      droppedHead: 0,
      droppedTail: 0,
      windowExceedsDuration: false
    }
  }

  const withWindow: EventWindow[] = []
  const withoutWindow: WeldSample[] = []

  for (const sample of samples) {
    const window = eventWindow(sample)
    if (window === null) {
      withoutWindow.push(sample)
    } else {
      withWindow.push({ sample, ...window })
    }
  }

  if (withWindow.length === 0) {
    return {
      kept: samples.slice(),
      droppedHead: 0,
      droppedTail: 0,
      windowExceedsDuration: false
    }
  }

  let weldStart = withWindow[0].startSec
  let weldEnd = withWindow[0].endSec
  for (const entry of withWindow) {
    if (entry.startSec < weldStart) weldStart = entry.startSec
    if (entry.endSec > weldEnd) weldEnd = entry.endSec
  }

  const weldDurationSec = weldEnd - weldStart
  const windowSec = inSec + outSec
  if (weldDurationSec < windowSec) {
    return {
      kept: samples.slice(),
      droppedHead: 0,
      droppedTail: 0,
      windowExceedsDuration: true
    }
  }

  const keepLower = weldStart + inSec
  const keepUpper = weldEnd - outSec

  let droppedHead = 0
  let droppedTail = 0
  const kept: WeldSample[] = []

  for (const sample of samples) {
    const window = eventWindow(sample)
    if (window === null) {
      kept.push(sample)
      continue
    }
    const { startSec, endSec } = window
    if (endSec > keepLower && startSec < keepUpper) {
      kept.push(sample)
      continue
    }
    if (endSec <= keepLower) {
      droppedHead += 1
    } else if (startSec >= keepUpper) {
      droppedTail += 1
    } else {
      // Straddles a boundary but fails strict overlap — classify by midpoint.
      const mid = (startSec + endSec) / 2
      if (mid < keepLower) droppedHead += 1
      else droppedTail += 1
    }
  }

  return { kept, droppedHead, droppedTail, windowExceedsDuration: false }
}

/**
 * Machine-clock seconds from TXT `Start Time` / `Time` (numeric strings).
 */
export function parseMachineSec(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null
  const trimmed = String(value).trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}

/**
 * Event interval on the machine weld clock: `[Start Time, Time]` with
 * `Start Time + Duration` as fallback for the end when `Time` is absent.
 */
export function eventWindow(
  sample: WeldSample
): { startSec: number; endSec: number } | null {
  const startSec = parseMachineSec(sample.startTime)
  if (startSec === null) return null

  const timeEnd = parseMachineSec(sample.sampleTime)
  let endSec: number | null = timeEnd
  if (endSec === null) {
    const duration = sample.duration
    if (duration !== null && Number.isFinite(duration)) {
      endSec = startSec + duration
    }
  }
  if (endSec === null) return null

  return { startSec, endSec }
}

function sanitizeSec(value: unknown): number {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.floor(n)
}
